import { store as persistentStore } from '../../store/Store';
import { isNativeEmbed } from './isNativeEmbed';

/**
 * Native token handoff.
 *
 * The iOS and Android apps open `{webApp}/app/<route>#authToken=<jwt>` in a WebView for every
 * screen they have not rewritten yet. The web app treats that token as a sign-in: it persists it
 * under `userToken`, drops it from the URL, and lets the private-route guard see a signed-in user
 * on the very first render.
 *
 * Two rules keep this from being a login-CSRF hole on a public origin, and both are load-bearing:
 *
 * - **The token travels in the fragment, never in the query.** A fragment is not sent to the
 *   server, so the JWT never lands in an access log of `music24.com` or of any proxy in front of
 *   it. A query parameter would be logged before any JavaScript could strip it.
 * - **Only the native WebView is believed.** Both apps append `Music24Native/<version> (ios|
 *   android)` to their WebView's user agent, and nothing else can set that header. Without the
 *   marker the fragment is thrown away and stripped, so a link mailed to somebody does not sign
 *   their browser into the sender's account.
 *
 * `captureNativeAuthToken` runs at module load, before React renders and before `ReduxLoader`
 * reads `userToken` out of the persistent store, so the store the app boots with already carries
 * the token. The dispatch that `NativeAuthTokenHandoff` does afterwards covers the case where the
 * Redux store already existed.
 */
export const authTokenParamName = 'authToken';

/** A token has to look like a JWT; anything else is ignored, not stored. */
const looksLikeJWT = (value: string): boolean => /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value);

// The user-agent check lives in its own module with no store import, so the theme code can read it
// before the app boots. It stays exported from here for the current callers.
export { isNativeEmbed };

/**
 * The parameters of a URL fragment. `#a=1&b=2` reads like a query string.
 *
 * Anything that is not a fragment reads as empty, so a query string handed here by mistake can
 * never be taken for one.
 */
function fragmentParams(hash: string): URLSearchParams {
  if (!hash.startsWith('#')) {
    return new URLSearchParams();
  }
  return new URLSearchParams(hash.slice(1));
}

/** Whether the fragment names a token at all, whatever its value is. */
export function hasAuthTokenFragment(hash: string): boolean {
  return fragmentParams(hash).has(authTokenParamName);
}

/** The JWT the fragment carries, or null when there is none or it is not a JWT. */
export function extractAuthToken(hash: string): string | null {
  const token = fragmentParams(hash).get(authTokenParamName);
  if (token === null || !looksLikeJWT(token)) {
    return null;
  }
  return token;
}

/** Same url with `authToken` removed from the fragment; every other parameter stays put. */
export function stripAuthTokenFromUrl(href: string, base: string): string {
  const url = new URL(href, base);
  const params = fragmentParams(url.hash);
  params.delete(authTokenParamName);
  const rest = params.toString();
  return `${url.pathname}${url.search}${rest === '' ? '' : `#${rest}`}`;
}

let capturedToken: string | null = null;

export function captureNativeAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!hasAuthTokenFragment(window.location.hash)) {
    return null;
  }
  const token = extractAuthToken(window.location.hash);
  const trusted = isNativeEmbed(window.navigator.userAgent);
  // The fragment goes whatever happens next. A token this page refuses must not stay in the
  // address bar, where a copied link would carry it on to somebody else.
  window.history.replaceState(null, '', stripAuthTokenFromUrl(window.location.href, window.location.origin));
  if (token === null || !trusted) {
    return null;
  }
  capturedToken = token;
  persistentStore.set('userToken', token).catch(console.error);
  return token;
}

/** Returns the captured token once, so a re-render cannot dispatch it twice. */
export function consumeNativeAuthToken(): string | null {
  const token = capturedToken;
  capturedToken = null;
  return token;
}
